<script setup lang="ts">
import { onBeforeMount } from 'vue';
import axios from 'axios';
import { useRouter, useRoute } from 'vue-router';
import { AppHeader } from '@ds/components';
import SidebarNavigation from '@shared/components/SidebarNavigation.vue';
import CreateMerchant from '@shared/components/CreateMerchant.vue';
import { useUserStore } from './store';
import { MerchantDropdown, LandingMessage } from './components';

type ClientCredentials = {
	client_id: string;
	credential: string;
	select_by: string;
};

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const GLOBAL_PAGES = [
	'bankIntegration',
	'division-requests',
	'kycChecks',
	'payout-request-details',
	'payout-requests',
	'payouts',
	'pspIntegration',
];

let checkExpiredTimeInterval: number;
let checkExpiredJWTToken: number;

const logout = async () => {
	userStore.logoutUser();
	await router.push({ name: 'login' });
};

const updateExpiredTimeHandler = () => {
	if (localStorage.getItem('isUserLoggedIn') === 'true' && userStore.isUserLoggedIn)
		localStorage.setItem('_expiredTime', JSON.stringify(Date.now() + 1.2e6)); // 20min for expiring session
};

const refreshJWT = () => {
	window?.google?.accounts.id.initialize({
		client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
		auto_select: true,
		callback: async (response: ClientCredentials) => {
			axios.defaults.headers.common = { Authorization: `${response.credential}` };
			localStorage.setItem('JWT', response.credential);
		},
	});

	window?.google?.accounts.id.prompt();
	localStorage.setItem('_expiredJWT', JSON.stringify(Date.now() + 3.3e6)); // 3.3e6 55min for auto-refresh session
};

onBeforeMount(() => {
	if (
		!localStorage.getItem('_expiredTime') ||
		parseInt(localStorage.getItem('_expiredTime') || '0', 10) < Date.now() ||
		localStorage.getItem('isUserLoggedIn') === 'false'
	)
		logout();

	const cachedUserInfo = localStorage.getItem('userInfo');
	const cachedJWT = localStorage.getItem('JWT');

	if (cachedUserInfo && cachedJWT) userStore.storeUser(JSON.parse(cachedUserInfo), cachedJWT);

	// Start listeners checking for inactive user
	window.addEventListener('mousemove', updateExpiredTimeHandler);
	window.addEventListener('scroll', updateExpiredTimeHandler);
	window.addEventListener('keydown', updateExpiredTimeHandler);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	checkExpiredTimeInterval = setInterval(() => {
		const userIsLoggedIn =
			localStorage.getItem('isUserLoggedIn') &&
			localStorage.getItem('isUserLoggedIn') === 'true';

		const expiredTime = parseInt(localStorage.getItem('_expiredTime') || '0', 10);
		if (expiredTime < Date.now() && userIsLoggedIn) logout();
	}, 5000);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	checkExpiredJWTToken = setInterval(() => {
		const userIsLoggedIn =
			localStorage.getItem('isUserLoggedIn') &&
			localStorage.getItem('isUserLoggedIn') === 'true';

		const expiredJWT = parseInt(localStorage.getItem('_expiredJWT') || '0', 10);
		if (expiredJWT < Date.now() && userIsLoggedIn) refreshJWT();
	}, 5000);
});
</script>

<template>
	<div class="app-container">
		<SidebarNavigation v-if="userStore.isUserLoggedIn" :is-showing-homepage="false" />
		<main class="app-container__main">
			<app-header
				v-if="userStore.isUserLoggedIn"
				:title="$route?.meta?.title as string || ''"
				:user-name="userStore.givenName"
				:user-email="userStore.email"
				:imageUrl="userStore.picture"
				:logout-label="$t('globals.logout')"
				@logout="logout"
			>
				<CreateMerchant class="mr-4" />
				<MerchantDropdown class="mr-4" />
			</app-header>
			<LandingMessage
				v-if="
					userStore.isUserLoggedIn &&
					!route.query?.merchantCode &&
					!GLOBAL_PAGES.includes($route.name?.toString() || '')
				"
			/>
			<router-view v-else />
		</main>
	</div>
</template>

<style lang="scss" scoped>
.app-container {
	height: 100vh;
	width: 100vw;
	overflow: hidden;

	display: grid;
	grid-template-columns: auto 1fr;
	grid-template-areas: 'nav-bar main-view';
	background-color: black;

	&__main {
		grid-area: main-view;
		overflow: hidden;
		position: relative;
		background-color: $color-gray-50;
	}
}
</style>
